
import React, { useMemo } from 'react';
import { GitFork, ChevronRight, CheckCircle, Split, Merge, AlertTriangle, ArrowRight, XCircle } from 'lucide-react';
import { AppNode, Edge } from '../../../types';

interface GatewaySimulationFormProps {
  node: AppNode;
  edges: Edge[];
  onNext: (data: any) => void;
  suggestedPath: string | null;
  visitedEdgeIds?: string[];
  executionQueueCount?: number;
  executionQueue?: string[];
  onProcessQueue?: () => void;
}

export const GatewaySimulationForm: React.FC<GatewaySimulationFormProps> = ({ 
    node, 
    edges, 
    onNext, 
    suggestedPath, 
    visitedEdgeIds = [], 
    executionQueueCount = 0,
    executionQueue = [],
    onProcessQueue 
}) => {
  
  const isParallel = node.type === 'ParallelGateway';
  const isMerging = node.data.isMerging;

  // --- Reachability Analysis ---
  // A helper to find all nodes reachable from a set of start nodes via standard sequence flows
  const getReachableNodes = (startNodeIds: string[], allEdges: Edge[]) => {
    const reachable = new Set<string>();
    const stack = [...startNodeIds];
    
    while (stack.length > 0) {
      const current = stack.pop()!;
      if (reachable.has(current)) continue;
      reachable.add(current);
      
      // Follow outgoing sequence flows (ignore boundary associations)
      const outgoing = allEdges.filter(e => e.source === current && e.sourceHandle !== 'boundary');
      outgoing.forEach(e => stack.push(e.target));
    }
    return reachable;
  };

  // Determine if unvisited incoming edges can still ever be triggered
  const mergeStatus = useMemo(() => {
    if (!isMerging) return null;

    const incomingEdges = edges.filter(e => e.target === node.id);
    const visited = incomingEdges.filter(e => visitedEdgeIds.includes(e.id));
    const unvisited = incomingEdges.filter(e => !visitedEdgeIds.includes(e.id));

    // Active tokens are those currently waiting in the execution queue
    // (The current node is the gateway itself, so it doesn't count as a source for its own unvisited inputs)
    const reachableFromQueue = getReachableNodes(executionQueue, edges);

    const deadPaths = unvisited.filter(edge => !reachableFromQueue.has(edge.source));
    const potentiallyActivePaths = unvisited.filter(edge => reachableFromQueue.has(edge.source));

    // Ready if no unvisited path is reachable from any other active token in the system
    const isReady = potentiallyActivePaths.length === 0;

    return {
      incomingEdges,
      visited,
      unvisited,
      deadPaths,
      potentiallyActivePaths,
      isReady
    };
  }, [node.id, edges, visitedEdgeIds, executionQueue, isMerging]);

  // --- Parallel Split Logic ---
  if (isParallel && !isMerging) {
      return (
        <div className="flex flex-col h-full bg-white dark:bg-slate-900">
            <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full flex items-center justify-center mb-4 shadow-sm">
                    <Split size={24} className="text-teal-500"/>
                </div>
                <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Parallel Split</h4>
                <p className="text-xs text-slate-500 mb-6 px-4">
                    Splitting into multiple paths. Simulator queues pending paths.
                </p>
                <div className="w-full bg-slate-50 dark:bg-slate-950 p-3 rounded-lg border border-slate-200 dark:border-slate-800 text-xs font-mono text-slate-600 dark:text-slate-300">
                    All outgoing paths activated.
                </div>
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex justify-end">
                <button 
                    onClick={() => onNext({})}
                    className="w-full px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold flex items-center justify-center gap-2 shadow-sm transition-all text-sm"
                >
                    Execute Split
                    <Split size={16} />
                </button>
            </div>
        </div>
      );
  }

  // --- Merge Logic ---
  if (isMerging && mergeStatus) {
      const { visited, incomingEdges, unvisited, deadPaths, potentiallyActivePaths, isReady } = mergeStatus;
      
      return (
        <div className="flex flex-col h-full bg-white dark:bg-slate-900">
            <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full flex items-center justify-center mb-4 shadow-sm">
                    <Merge size={24} className={isReady ? "text-emerald-500" : "text-amber-500"}/>
                </div>
                
                <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                    {isReady ? "Merge Synchronized" : "Waiting for Tokens"}
                </h4>
                
                <div className="flex items-center gap-2 mb-6">
                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${isReady ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30'}`}>
                        {visited.length} / {incomingEdges.length} Flows Received
                    </span>
                </div>

                <div className="w-full space-y-4 text-left">
                    {/* Status Summary */}
                    {!isReady ? (
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-lg">
                            <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                                <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                                <span className="text-xs font-bold">Incomplete Join</span>
                            </div>
                            <p className="text-[10px] text-amber-600 dark:text-amber-500 mt-1">
                                {potentiallyActivePaths.length} incoming path(s) are still reachable from other active tokens in the process.
                            </p>
                        </div>
                    ) : visited.length < incomingEdges.length ? (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/50 rounded-lg">
                            <div className="flex items-start gap-2 text-blue-700 dark:text-blue-400">
                                <CheckCircle size={14} className="mt-0.5 shrink-0"/>
                                <span className="text-xs font-bold">Dead-Path Elimination</span>
                            </div>
                            <p className="text-[10px] text-blue-600 dark:text-blue-500 mt-1">
                                Reachability analysis confirms no other tokens can trigger the remaining {unvisited.length} inputs. Proceeding.
                            </p>
                        </div>
                    ) : null}

                    {/* Path Details */}
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Input Connectivity</label>
                        {incomingEdges.map((edge, i) => {
                            const isVisited = visited.includes(edge);
                            const isDead = deadPaths.includes(edge);
                            return (
                                <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 text-[10px]">
                                    <span className="font-mono text-slate-500">Input #{i+1}</span>
                                    {isVisited ? (
                                        <span className="text-emerald-600 font-bold flex items-center gap-1"><CheckCircle size={10}/> RECEIVED</span>
                                    ) : isDead ? (
                                        <span className="text-slate-400 font-medium flex items-center gap-1"><XCircle size={10}/> UNREACHABLE</span>
                                    ) : (
                                        <span className="text-amber-600 font-bold flex items-center gap-1 animate-pulse"><ArrowRight size={10}/> PENDING</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex flex-col gap-3">
                {!isReady && executionQueueCount > 0 && onProcessQueue && (
                    <button 
                        onClick={onProcessQueue}
                        className="w-full px-4 py-2.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold flex items-center justify-center gap-2 transition-all text-sm shadow-sm"
                    >
                        Switch to Pending Branch ({executionQueueCount})
                        <ArrowRight size={14} />
                    </button>
                )}
                
                <button 
                    onClick={() => onNext({})}
                    disabled={!isReady}
                    className="w-full px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold flex items-center justify-center gap-2 shadow-sm transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Proceed with Flow
                    <Merge size={16} />
                </button>
            </div>
        </div>
      );
  }

  // --- Standard Decision Logic (XOR / Inclusive / etc) ---
  let outputs: string[] = node.data.outputs || [];
  
  if (outputs.length === 0) {
      const outgoingEdges = edges.filter(e => e.source === node.id);
      outputs = outgoingEdges.map((e, i) => {
          const label = typeof e.label === 'string' ? e.label : undefined;
          return label || `Path ${i + 1}`;
      });
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <div className="flex-1 overflow-y-auto p-5">
        <div className="space-y-4">
            <div className="text-center mb-6 pt-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 mb-3 shadow-sm">
                    <GitFork size={20} className="text-slate-400"/>
                </div>
                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300">Make Decision</h4>
                <p className="text-xs text-slate-500">Select a path to continue.</p>
            </div>
            
            <div className="grid gap-3">
                {outputs.map((output: string, idx: number) => {
                    const isSuggested = suggestedPath === String(idx);
                    return (
                        <button 
                        key={idx}
                        onClick={() => onNext({ selectedPath: idx })}
                        className={`flex items-center justify-between p-3 rounded-xl border transition-all group text-left
                            ${isSuggested 
                                ? 'border-teal-500 ring-1 ring-teal-500/20 bg-teal-50/10 dark:bg-teal-900/10 shadow-sm' 
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900 shadow-sm'}`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold border ${isSuggested ? 'bg-teal-100 dark:bg-teal-900/50 border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-400' : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500'}`}>
                                    {idx + 1}
                                </div>
                                <div className="min-w-0">
                                    <div className={`font-bold text-sm truncate ${isSuggested ? 'text-teal-900 dark:text-teal-100' : 'text-slate-700 dark:text-slate-200'}`}>{output}</div>
                                </div>
                            </div>
                            {isSuggested ? (
                                <span className="text-[10px] font-bold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30 px-2 py-1 rounded-full flex items-center gap-1 shrink-0">
                                    <CheckCircle size={10}/>
                                </span>
                            ) : (
                                <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 shrink-0" />
                            )}
                        </button>
                    )
                })}
            </div>
        </div>
      </div>
    </div>
  );
};
